import type { CSSProperties, JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { Text } from '@feathr/components';
import { cssVar } from '@feathr/hooks';

import type { IBreakdownData } from '../DonationFormReport.utils';
import { formatCount, formatCurrency } from '../DonationFormReport.utils';

import * as styles from './DonationTypeDistributionChart.css';

export interface IDonationTypeDistributionChartProps {
  /** Chart-ready breakdown produced by `buildBreakdownData`. */
  data: IBreakdownData;
}

interface ISlice {
  name: string;
  count: number;
  total: number;
  color: string;
}

function tooltipFormatter(
  value: number | string | Array<number | string>,
  _name: unknown,
  item: { payload?: ISlice } | undefined,
): ReactNode {
  if (typeof value !== 'number') {
    return String(value);
  }
  const total = item?.payload?.total ?? 0;
  return `${formatCount(value)} · ${formatCurrency(total)}`;
}

/**
 * Donation Type Distribution — recharts PieChart donut splitting donations
 * into one-time (sky-600) and monthly/recurring (emerald-600) slices, with a
 * side table echoing the counts and totals. Per Figma node 10162:12707.
 */
function DonationTypeDistributionChart({
  data,
}: Readonly<IDonationTypeDistributionChartProps>): JSX.Element {
  const { t } = useTranslation();
  const oneTimeColor = cssVar('--color-sky-600');
  const monthlyColor = cssVar('--color-emerald-600');

  const slices: ISlice[] = [
    {
      name: t('One time'),
      count: data.oneTime.count,
      total: data.oneTime.total,
      color: oneTimeColor,
    },
    {
      name: t('Monthly'),
      count: data.monthly.count,
      total: data.monthly.total,
      color: monthlyColor,
    },
  ];

  return (
    <div className={styles.root} data-name={'donation-type-distribution-chart'}>
      <div className={styles.heading}>
        <Text.DefaultHeadingMedium>{t('Donation Type Distribution')}</Text.DefaultHeadingMedium>
        <Text.SmallRegular>{t('Breakdown by donation frequency')}</Text.SmallRegular>
      </div>
      <div className={styles.body}>
        <div className={styles.chartContainer}>
          <ResponsiveContainer height={200} width={'99%'}>
            <PieChart>
              <Pie
                data={slices}
                dataKey={'count'}
                innerRadius={60}
                nameKey={'name'}
                outerRadius={80}
                paddingAngle={2}
                stroke={'none'}
              >
                {slices.map((slice) => (
                  <Cell fill={slice.color} key={slice.name} />
                ))}
              </Pie>
              <Tooltip formatter={tooltipFormatter} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className={styles.table} data-name={'donation-type-distribution-table'}>
          <div className={styles.row}>
            <Text inline={true} variant={'smallMedium'}>
              {t('Type')}
            </Text>
            <Text className={styles.numericCell} inline={true} variant={'smallMedium'}>
              {t('Donations')}
            </Text>
            <Text className={styles.numericCell} inline={true} variant={'smallMedium'}>
              {t('Total')}
            </Text>
          </div>
          {slices.map((slice) => (
            <div className={styles.row} key={slice.name}>
              <div className={styles.typeCell}>
                <span
                  className={styles.swatch}
                  style={{ '--p-swatch-color': slice.color } as CSSProperties}
                />
                <Text inline={true} variant={'smallRegular'}>
                  {slice.name}
                </Text>
              </div>
              <Text className={styles.numericCell} inline={true} variant={'smallRegular'}>
                {formatCount(slice.count)}
              </Text>
              <Text className={styles.numericCell} inline={true} variant={'smallRegular'}>
                {formatCurrency(slice.total)}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DonationTypeDistributionChart;
